import { useSearchParams } from 'react-router-dom'
import './styles.scss'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useViewSimpleMovement } from './hooks/useViewSimpleMovement'
import { ReceptionTable } from './components/ReceptionTable'
import mySkyProFooterImage from '../../assets/imagesNew/my-sky-pro-footer-OSR.png'
import loadingGif from '../../assets/imagesNew/loading.gif'
import { useUpdateSimpleMovement } from './hooks/useUpdateSimpleMovement'
import { validateFloat } from './utils/validateFloat'
import { MOVEMENT_STATUS } from '../../infra/constants/MovementStatus'
import { COMPONENT_STATUS } from '../../infra/constants/ComponentStatus'
import { useTranslation } from 'react-i18next'

const OrderSupplierReception = () => {
  const { t } = useTranslation()
  const [params] = useSearchParams()
  const date = params.get('DATE')
  const id = params.get('MID')

  const year = new Date().getFullYear()

  const { movementData, isFetching, error } = useViewSimpleMovement(id)
  const { updateMovement, isUpdating } = useUpdateSimpleMovement(id)

  const [rows, setRows] = useState([])
  const [submitted, setSubmitted] = useState(false)
  const [status, setStatus] = useState(COMPONENT_STATUS.IDLE) // 'success'|'warning'|'error'

  const itemRows = useMemo(() => movementData?.itemRows || [], [movementData?.itemRows])
  const movementStatus = useMemo(() => movementData?.movementStatus, [movementData?.movementStatus])
  const movementDate = useMemo(() => movementData?.date, [movementData?.date])

  useEffect(() => {
    let isMounted = true
    if (!isMounted || isFetching) return

    if (error || (movementDate && movementDate !== date)) setStatus(COMPONENT_STATUS.ERROR)
    else if (movementStatus && movementStatus !== MOVEMENT_STATUS.SHIPPED) setStatus(COMPONENT_STATUS.WARNING)
    else if (movementStatus === MOVEMENT_STATUS.SHIPPED) setStatus(COMPONENT_STATUS.IDLE)

    if (itemRows && itemRows.length > 0)
      setRows(
        itemRows.map(r => ({
          barcode: r.barcode ?? '',
          productName: r.productName ?? '',
          productReferenceCode: r.productReferenceCode ?? '',
          pendingQuantity: Number(r.pendingQuantity ?? r.plannedQuantity ?? 0),
          receivedQuantity: r.receivedQuantity ?? ''
        }))
      )
    return () => isMounted = false
  }, [itemRows])

  const handleQuantityChange = useCallback((rowIndex, value) => {
    setRows(prev => {
      if (!prev[rowIndex]) return prev

      const next = [...prev]

      if (value === '') {
        next[rowIndex] = { ...prev[rowIndex], receivedQuantity: '', __invalid: false }
        return next
      }

      const newValue = Number(value)
      if (!Number.isFinite(newValue) || newValue < 0) return prev

      next[rowIndex] = { ...prev[rowIndex], receivedQuantity: newValue, __invalid: false }
      return next
    })
  }, [])

  const handleSubmit = useCallback(() => {
    if (submitted) return

    let isValidForm = true
    const invalidIdx = []

    const payloadItemRows = rows.map((r, i) => {
      const qStr = r.receivedQuantity === '' || r.receivedQuantity == null ? '' : String(r.receivedQuantity)
      if (!validateFloat(qStr)) {
        isValidForm = false
        invalidIdx.push(i)
      }
      return { barcode: r.barcode, UpdatedQuantity: parseFloat(qStr || '0') }
    })

    if (!isValidForm) {
      setRows(prev => prev.map((r, i) => ({ ...r, __invalid: invalidIdx.includes(i) })))
      return
    }

    setSubmitted(true)

    updateMovement(
      { movementStatus: '', movementType: '', ItemRows: payloadItemRows },
      { onSuccess: () => setStatus(COMPONENT_STATUS.SUCCESS), onError: () => setStatus(COMPONENT_STATUS.ERROR) }
    )
  }, [rows, submitted, updateMovement])

  return (
    <div className="container">

      {isFetching && (
        <div className="loading">
          <p>
            <img src={loadingGif} alt="Loading..." width="100" />
          </p>
        </div>
      )}

      {(!isFetching && status !== COMPONENT_STATUS.IDLE) && (
        <div className={status}>
          <h2>{t(`STATUS_${status.toUpperCase()}_TITLE`)}</h2>
          <p>{t(`STATUS_${status.toUpperCase()}_TEXT`)}</p>
        </div>
      )}

      {(!isFetching && status === COMPONENT_STATUS.IDLE && itemRows.length > 0) && (
        <>
          <h1 className='tableTitle'>{t('UPDATE_QUANTITIES_TITLE')}</h1>
          <ReceptionTable
            data={rows}
            onQuantityChange={handleQuantityChange}
          />
          <div className='buttons'>
            <button
              className="submitBtn"
              onClick={handleSubmit}
              disabled={isUpdating || submitted}
            >
              {t('CONFIRM_RECEPTION')}
            </button>
            <img src="images/loading.gif" alt="Loading..." width="36" className="submitLoading" style={{ display: isUpdating ? 'inline-block' : 'none' }} />
          </div>
        </>
      )}

      <div className='logo'>
        <p>
          <a href="https://backoffice.wearskypro.com" title="mySKYPRO Portal" >
            <img src={mySkyProFooterImage} alt="mySKYPRO" />
          </a>
        </p>
        <p>
          {t('ALL_RIGHTS_RESERVED', { year })}
        </p>
      </div>
    </div>
  )
}

export default OrderSupplierReception